import React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import { Input, Form, Modal, Radio, Space, Button, Flex, Typography, Tag, Divider, message } from 'antd';
import { TypedUseMutationResult } from '@reduxjs/toolkit/dist/query/react';

import { useLazyGetURLsCountQuery } from '../../urls/api/urlsApiSlice';
import { useEvent } from '../../events/hooks/useEvent';
import { usePrerenderUser } from '../../../hooks/usePrerenderUser';
import {
  useCreateURLParameterMutation,
  useUpdateURLParameterMutation,
  useDeleteURLParameterMutation,
} from '../CacheManagerApi';
import URLParameter from './URLParameter';

const wildcardToRegex = (rule: string) => {
  const escapeRegex = (u: string) => u.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1'); // eslint-disable-line
  return new RegExp(`^${rule.split('*').map(escapeRegex).join('.*')}$`);
};

function exactWildcardMatch(text = '', pattern = '') {
  const regExp = wildcardToRegex(pattern);
  return regExp.test(text);
}

function MatchTag({ text, pattern }: { text: string; pattern: string }) {
  const isEmpty = !text || !pattern;
  if (isEmpty) return null;

  const match = exactWildcardMatch(text, pattern);

  if (match) return <Tag color="success">Match</Tag>;
  return <Tag color="error">No match</Tag>;
}

function UrlAffected({ count, isLoading, isEmpty }: { count: number; isLoading: boolean; isEmpty: boolean }) {
  if (isEmpty) return <Typography.Paragraph>Number of URLs affected: start typing to find out</Typography.Paragraph>;
  if (isLoading) return <Typography.Paragraph>Number of URLs affected: calculating...</Typography.Paragraph>;
  return <Typography.Paragraph>Number of URLs affected: {count}</Typography.Paragraph>;
}

const ALLOWED_PARAM_NAME_PATTERN = /^[a-zA-Z0-9-_*]*$/;
const SPECIAL_CHARS_ERROR = 'Only letters, numbers, hyphens, and asterisks are allowed';
const DEFAULT_ERROR = "Please only enter the name of the query parameter that needs to be ignored e.g. 'page'";

const enum Rule {
  Ignore = 'ignore',
  Allow = 'allow',
}

type UrlParamEditorProps = {
  urlParam?: URLParameter;
  open: boolean;
  onClose: () => void;
};

const UrlParamEditor = ({ urlParam, open, onClose }: UrlParamEditorProps) => {
  const [testParamInput, setTestParamInput] = useState('');

  const { track } = useEvent();
  const prerenderUser = usePrerenderUser();

  const [getURLsCount, urlsCountResult] = useLazyGetURLsCountQuery();
  const [createURLParameter, createURLParameterResult] = useCreateURLParameterMutation();
  const [updateURLParameter, updateURLParameterResult] = useUpdateURLParameterMutation();
  const [deleteURLParameter, deleteURLParameterResult] = useDeleteURLParameterMutation();

  const [form] = Form.useForm();
  const latestRequestRef = useRef<TypedUseMutationResult<any, any, any, any>>(null);
  const [messageApi, messageContext] = message.useMessage();

  const urlParamName = Form.useWatch('param', { form, preserve: true });
  const isEditMode = typeof urlParam?.id === 'number';

  React.useEffect(() => {
    if (createURLParameterResult.isError) messageApi.error('Failed to create URL parameter. Please try again later.');
    if (updateURLParameterResult.isError) messageApi.error('Failed to update URL parameter. Please try again later.');
    if (deleteURLParameterResult.isError) messageApi.error('Failed to delete URL parameter. Please try again later.');
  }, [createURLParameterResult.isError, updateURLParameterResult.isError, deleteURLParameterResult.isError]);

  const onModalCancel = () => {
    setTestParamInput('');
    form.resetFields();
    onClose();
  };

  const onModalSubmit = async ({ param = '', rule }: { param: string; rule: Rule }) => {
    try {
      if (isEditMode) {
        const { id } = urlParam;
        await updateURLParameter([{ id, value: param, ignore: rule === Rule.Ignore }]).unwrap();
        track('URL Parameter Rule Updated', {
          subscription_plan: prerenderUser.chargebeePlanId,
          is_bulk_action: false,
          caching_rule: rule === Rule.Ignore ? 'ignore when caching' : 'use when caching',
        });
      } else {
        await createURLParameter({ value: param, ignore: rule === Rule.Ignore }).unwrap();
        track('URL Parameter Added', {
          subscription_plan: prerenderUser.chargebeePlanId,
          caching_rule: param,
        });
      }
      onClose();
    } catch {
      // Ignore update/create errors
    }
  };

  const onDeleteURL = async (id: URLParameter['id']) => {
    try {
      await deleteURLParameter([id]).unwrap();
      track('URL Parameter Deleted', {
        subscription_plan: prerenderUser.chargebeePlanId,
        is_bulk_action: false,
      });
      onClose();
    } catch {
      // Ignore delete errors
    }
  };

  const debouncedUrlCountQuery = useCallback(
    debounce((paramName?: string) => {
      if (latestRequestRef.current) {
        latestRequestRef.current.abort();
        latestRequestRef.current = null;
      }
      if (paramName) latestRequestRef.current = getURLsCount({ wildcard: paramName, wildcard_type: 'query' });
    }, 500),
    []
  );

  async function triggerUrlCountQuery(urlParamName: string) {
    try {
      await form.validateFields();
      debouncedUrlCountQuery(urlParamName);
    } catch (error) {
      debouncedUrlCountQuery();
    }
  }

  const onTestParamInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => setTestParamInput(e.target.value);

  useEffect(() => {
    if (urlParam) {
      const activeRule = urlParam.id ? (urlParam.ignore ? Rule.Ignore : Rule.Allow) : Rule.Ignore;

      form.setFieldsValue({ rule: activeRule, param: urlParam.value });
    }
  }, [urlParam, form]);

  useEffect(() => {
    if (open) triggerUrlCountQuery(urlParamName);
  }, [urlParamName, form, open]);

  return (
    <>
      {messageContext}
      <Modal
        destroyOnHidden
        closable
        open={open}
        title="Add caching rule"
        okText="Save"
        cancelText="Cancel"
        onCancel={onModalCancel}
        okButtonProps={{
          htmlType: 'submit',
          form: 'addUrlParam',
          loading: createURLParameterResult.isLoading || updateURLParameterResult.isLoading,
        }}
        styles={{ footer: { display: 'flex', justifyContent: 'space-between' } }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            <CancelBtn />
            <Flex gap="small">
              {!!isEditMode && (
                <Button onClick={() => onDeleteURL(urlParam.id)} loading={deleteURLParameterResult.isLoading} danger>
                  Delete from list
                </Button>
              )}
              <OkBtn />
            </Flex>
          </>
        )}
      >
        {open && (
          <Form name="addUrlParam" form={form} style={{ marginTop: 12 }} layout="vertical" onFinish={onModalSubmit}>
            <Form.Item
              style={{ marginTop: 4, marginBottom: 6 }}
              name="param"
              label="Rule string"
              rules={[
                {
                  required: true,
                  message: DEFAULT_ERROR,
                },
                {
                  max: 50,
                  message: DEFAULT_ERROR,
                },
                {
                  pattern: ALLOWED_PARAM_NAME_PATTERN,
                  message: SPECIAL_CHARS_ERROR,
                },
              ]}
            >
              <Input id="param" placeholder="Enter a URL query parameter" autoFocus value={urlParamName} showCount />
            </Form.Item>
            <div>
              <UrlAffected
                count={urlsCountResult.data?.count}
                isLoading={urlsCountResult.isFetching}
                isEmpty={!urlParamName}
              />
            </div>
            <div style={{ marginTop: 4, marginBottom: 24 }}>
              <Typography.Paragraph type="secondary" style={{ marginBottom: '4px' }}>
                If you are using a wildcard, you can try it here
              </Typography.Paragraph>
              <Space.Compact block>
                <Space.Addon>
                  <div style={{ width: '70px' }}>
                    <MatchTag pattern={urlParamName} text={testParamInput} />
                  </div>
                </Space.Addon>
                <Input placeholder="test-query-param" value={testParamInput} onChange={onTestParamInputChange} />
              </Space.Compact>
            </div>

            <Divider />

            <Form.Item name="rule" label="Caching rule" initialValue={Rule.Ignore}>
              <Radio.Group>
                <Space direction="vertical">
                  <Radio value={Rule.Ignore}>Ignore when Caching</Radio>
                  <Radio value={Rule.Allow}>Use when Caching</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default UrlParamEditor;
